import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikConfig } from 'formik'

import { transformHoursToTwoDigitsString } from 'app/utils/dates'
import { CartFormValues } from 'appTypes/orders'
import { generateNewOrderValidation, generateProlongationValidation } from 'helpers/validationSchemas/order'

import { RentProductLoginSubmitHandler } from '../interfaces'

type Props = {
  onSubmit: RentProductLoginSubmitHandler
  isProlonging: boolean
  rentedFrom: string
  rentedTo: string
  isCleaning: boolean
  isInsurance: boolean
  locationId: number | undefined
  regionId: number | undefined
  managerId: number | undefined
  amount: number | undefined
}

export const useRentProductLogicFormikOptions = ({
  onSubmit,
  isProlonging,
  rentedFrom,
  rentedTo,
  isCleaning,
  isInsurance,
  locationId,
  regionId,
  managerId,
  amount,
}: Props): FormikConfig<CartFormValues> => {
  const { t } = useTranslation()

  const initialValues = useMemo((): CartFormValues => {
    const includeCleaning = isCleaning
    const includeInsurance = isInsurance

    const rentedFromDate = rentedFrom ? new Date(rentedFrom) : null
    const rentedToDate = rentedTo ? new Date(rentedTo) : null
    const pickupAt = rentedFromDate ? transformHoursToTwoDigitsString(rentedFromDate.getHours()) : null
    const returnAt = rentedToDate ? transformHoursToTwoDigitsString(rentedToDate.getHours()) : null

    const commonValues: CartFormValues = {
      startDate: rentedFromDate ? new Date(rentedFromDate.setHours(0, 0, 0, 0)) : null,
      endDate: rentedToDate ? new Date(rentedToDate.setHours(0, 0, 0, 0)) : null,
      pickupAt: pickupAt,
      returnAt: returnAt,
      includeCleaning: includeCleaning,
      includeInsurance: includeInsurance,
      selectedLocationId: locationId,
      selectedRegionId: regionId,
      managerId: managerId,
      hasServices: false,
      amount: amount ?? 1,
    }

    return {
      complementaryItemsQuantity: [],
      ...commonValues,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actualOnSubmit: FormikConfig<CartFormValues>['onSubmit'] = (values, helpers) => {
    onSubmit(values, helpers, initialValues)
  }

  return {
    initialValues,
    onSubmit: actualOnSubmit,
    validationSchema: isProlonging ? generateProlongationValidation(t) : generateNewOrderValidation(t),
  }
}
